import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { 
  AccountShareWithProfiles, 
  ShareInvitationWithInviter,
  PermissionLevel,
  ShareStatus,
  AcceptShareResult,
  Profile 
} from '@/integrations/supabase/types';

// Re-export types for consumers
export type { PermissionLevel, ShareStatus, AcceptShareResult };

// Use the types from database with joined profiles
export type AccountShare = AccountShareWithProfiles;
export type ShareInvitation = ShareInvitationWithInviter;

export interface CreateInvitationParams {
  email: string;
  permission_level: PermissionLevel;
}

// ============================================
// NOTIFICATION HOOKS (preparado para futuro)
// ============================================

export interface SharingNotificationPayload {
  type: 'share_invitation_sent' | 'share_invitation_received' | 'share_accepted' | 'share_rejected' | 'share_revoked' | 'permission_changed';
  actor_id: string;
  target_id?: string;
  share_id?: string;
  invitation_id?: string;
  permission_level?: PermissionLevel;
  metadata?: Record<string, unknown>;
}

// Callback type para integração futura com sistema de notificações
type NotificationCallback = (payload: SharingNotificationPayload) => void | Promise<void>;

// Registry de callbacks para notificações
const notificationCallbacks: NotificationCallback[] = [];

/**
 * Registra um callback para ser chamado quando eventos de compartilhamento ocorrerem.
 * Use isso para integrar com um sistema de notificações futuro.
 * 
 * @example
 * ```ts
 * // No futuro, quando implementar notificações:
 * registerSharingNotificationCallback(async (payload) => {
 *   await sendPushNotification(payload);
 *   await saveNotificationToDatabase(payload);
 * });
 * ```
 */
export function registerSharingNotificationCallback(callback: NotificationCallback) {
  notificationCallbacks.push(callback);
  return () => {
    const index = notificationCallbacks.indexOf(callback);
    if (index > -1) notificationCallbacks.splice(index, 1);
  };
}

// Dispara notificações para todos os callbacks registrados
async function triggerNotification(payload: SharingNotificationPayload) {
  for (const callback of notificationCallbacks) {
    try {
      await callback(payload);
    } catch (error) {
      console.error('Error in sharing notification callback:', error);
    }
  }
}

// Helper para gerar token seguro
function generateToken(): string {
  const array = new Uint8Array(24);
  crypto.getRandomValues(array);
  return Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
}

// ============================================
// MAIN HOOK
// ============================================

export function useSharing() {
  const [myShares, setMyShares] = useState<AccountShare[]>([]);
  const [sharedWithMe, setSharedWithMe] = useState<AccountShare[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<ShareInvitation[]>([]);
  const [myInvitations, setMyInvitations] = useState<ShareInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const { toast } = useToast();

  // ============================================
  // FETCH FUNCTIONS
  // ============================================

  /**
   * Busca compartilhamentos onde o usuário é o dono (owner)
   */
  const fetchMyShares = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      setCurrentUserId(user.id);

      const { data, error } = await supabase
        .from('account_shares')
        .select(`
          *,
          shared_with:profiles!account_shares_shared_with_id_fkey(id, full_name, avatar_url)
        `)
        .eq('owner_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setMyShares((data || []) as AccountShare[]);
    } catch (error: any) {
      console.error('Error fetching my shares:', error);
    }
  }, []);

  /**
   * Busca compartilhamentos onde o usuário tem acesso (shared_with)
   */
  const fetchSharedWithMe = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('account_shares')
        .select(`
          *,
          owner:profiles!account_shares_owner_id_fkey(id, full_name, avatar_url)
        `)
        .eq('shared_with_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setSharedWithMe((data || []) as AccountShare[]);
    } catch (error: any) {
      console.error('Error fetching shared with me:', error);
    }
  }, []);

  /**
   * Busca convites pendentes para o email do usuário atual
   */
  const fetchPendingInvitations = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.email) return;

      const { data, error } = await supabase
        .from('share_invitations')
        .select(`
          *,
          inviter:profiles!share_invitations_inviter_id_fkey(id, full_name, avatar_url)
        `)
        .ilike('invitee_email', user.email)
        .is('used_at', null)
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false });

      if (error) throw error;

      setPendingInvitations((data || []) as ShareInvitation[]);
    } catch (error: any) {
      console.error('Error fetching pending invitations:', error);
    }
  }, []);

  /**
   * Busca convites enviados pelo usuário atual
   */
  const fetchMyInvitations = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('share_invitations')
        .select('*')
        .eq('inviter_id', user.id)
        .is('used_at', null)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setMyInvitations((data || []) as ShareInvitation[]);
    } catch (error: any) {
      console.error('Error fetching my invitations:', error);
    }
  }, []);

  /**
   * Busca todos os dados de compartilhamento
   */
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchMyShares(),
        fetchSharedWithMe(),
        fetchPendingInvitations(),
        fetchMyInvitations(),
      ]);
    } finally {
      setLoading(false);
    }
  }, [fetchMyShares, fetchSharedWithMe, fetchPendingInvitations, fetchMyInvitations]);

  // Initial fetch
  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // ============================================
  // MUTATION FUNCTIONS
  // ============================================

  /**
   * Cria um convite de compartilhamento por email
   */
  const createInvitation = async ({ email, permission_level }: CreateInvitationParams): Promise<ShareInvitation | null> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      // Verifica se já existe compartilhamento ou convite para este email
      const normalizedEmail = email.toLowerCase().trim();
      
      // Verifica se está tentando convidar a si mesmo
      if (user.email?.toLowerCase() === normalizedEmail) {
        throw new Error('Você não pode compartilhar sua conta com você mesmo');
      }

      // Verifica se já existe um convite pendente
      const existingInvitation = myInvitations.find(
        inv => inv.invitee_email.toLowerCase() === normalizedEmail
      );
      if (existingInvitation) {
        throw new Error('Já existe um convite pendente para este email');
      }

      // Verifica se o usuário já tem acesso (checando por email nas contas existentes)
      // Note: Não temos RPC get_user_id_by_email, então verificamos depois se existe convite
      // A lógica de verificação de usuário existente é feita quando o convite é aceito

      // Gera token único usando Web Crypto API (24 bytes → base64 URL-safe)
      const bytes = new Uint8Array(24);
      crypto.getRandomValues(bytes);
      const token = btoa(String.fromCharCode(...bytes))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');

      // Cria o convite com expiração de 7 dias
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);

      const { data, error } = await supabase
        .from('share_invitations')
        .insert({
          inviter_id: user.id,
          invitee_email: normalizedEmail,
          permission_level,
          token,
          expires_at: expiresAt.toISOString(),
        })
        .select()
        .single();

      if (error) {
        if (error.code === '23505') {
          throw new Error('Já existe um convite pendente para este email');
        }
        throw error;
      }

      // Envia email de convite via Edge Function
      try {
        // Busca nome do usuário para o email
        const { data: profile } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('id', user.id)
          .single();

        const { error: emailError } = await supabase.functions.invoke('send-share-email', {
          body: {
            type: 'share-invitation',
            data: {
              invitation_id: data.id,
              invitee_email: normalizedEmail,
              inviter_name: profile?.full_name || user.email?.split('@')[0] || 'Usuário',
              token,
              permission_level,
              expires_at: expiresAt.toISOString(),
            },
          },
        });

        if (emailError) {
          console.warn('Erro ao enviar email de convite:', emailError);
          // Não falha a criação do convite se o email falhar
        }
      } catch (emailErr) {
        console.warn('Erro ao chamar edge function de email:', emailErr);
      }

      // Trigger notification hook (para integração futura)
      await triggerNotification({
        type: 'share_invitation_sent',
        actor_id: user.id,
        invitation_id: data.id,
        permission_level,
        metadata: { invitee_email: normalizedEmail },
      });

      toast({
        title: 'Convite enviado',
        description: `Convite enviado para ${normalizedEmail}`,
      });

      await fetchMyInvitations();
      return data as ShareInvitation;
    } catch (error: any) {
      toast({
        title: 'Erro ao criar convite',
        description: error.message,
        variant: 'destructive',
      });
      return null;
    }
  };

  /**
   * Aceita um convite de compartilhamento usando o token
   */
  const acceptInvitation = async (token: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase.rpc('accept_share_invitation', {
        invitation_token: token,
      });

      if (error) throw error;

      const result = data as unknown as AcceptShareResult;
      
      if (!result.success) {
        throw new Error(result.error || 'Erro ao aceitar convite');
      }

      // Trigger notification hook
      const { data: { user } } = await supabase.auth.getUser();
      if (user && result.share_id) {
        await triggerNotification({
          type: 'share_accepted',
          actor_id: user.id,
          share_id: result.share_id,
        });

        // Envia email de notificação para o dono da conta
        try {
          // Busca o share para pegar o owner_id e permission_level
          const { data: share } = await supabase
            .from('account_shares')
            .select('owner_id, permission_level')
            .eq('id', result.share_id)
            .single();

          if (share) {
            await supabase.functions.invoke('send-share-email', {
              body: {
                type: 'share-accepted',
                data: {
                  owner_id: share.owner_id,
                  accepter_id: user.id,
                  permission_level: share.permission_level,
                },
              },
            });
          }
        } catch (emailErr) {
          console.warn('Erro ao enviar email de aceitação:', emailErr);
        }
      }

      toast({
        title: 'Convite aceito',
        description: result.message || 'Você agora tem acesso à conta compartilhada',
      });

      await fetchAll();
      return true;
    } catch (error: any) {
      toast({
        title: 'Erro ao aceitar convite',
        description: error.message,
        variant: 'destructive',
      });
      return false;
    }
  };

  /**
   * Aceita um compartilhamento direto (quando o usuário já existe)
   */
  const acceptShare = async (shareId: string): Promise<boolean> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const { error } = await supabase
        .from('account_shares')
        .update({
          status: 'accepted',
          accepted_at: new Date().toISOString(),
        })
        .eq('id', shareId)
        .eq('shared_with_id', user.id);

      if (error) throw error;

      // Trigger notification hook
      await triggerNotification({
        type: 'share_accepted',
        actor_id: user.id,
        share_id: shareId,
      });

      toast({
        title: 'Compartilhamento aceito',
        description: 'Você agora tem acesso à conta compartilhada',
      });

      await fetchAll();
      return true;
    } catch (error: any) {
      toast({
        title: 'Erro ao aceitar compartilhamento',
        description: error.message,
        variant: 'destructive',
      });
      return false;
    }
  };

  /**
   * Rejeita um compartilhamento
   */
  const rejectShare = async (shareId: string): Promise<boolean> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const { error } = await supabase
        .from('account_shares')
        .update({ status: 'rejected' })
        .eq('id', shareId)
        .eq('shared_with_id', user.id);

      if (error) throw error;

      // Trigger notification hook
      await triggerNotification({
        type: 'share_rejected',
        actor_id: user.id,
        share_id: shareId,
      });

      toast({
        title: 'Compartilhamento recusado',
      });

      await fetchAll();
      return true;
    } catch (error: any) {
      toast({
        title: 'Erro ao recusar compartilhamento',
        description: error.message,
        variant: 'destructive',
      });
      return false;
    }
  };

  /**
   * Revoga um compartilhamento (owner remove acesso de alguém)
   */
  const revokeShare = async (shareId: string): Promise<boolean> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      // Busca o share para notificação
      const share = myShares.find(s => s.id === shareId);

      const { error } = await supabase
        .from('account_shares')
        .delete()
        .eq('id', shareId)
        .eq('owner_id', user.id);

      if (error) throw error;

      // Trigger notification hook
      await triggerNotification({
        type: 'share_revoked',
        actor_id: user.id,
        share_id: shareId,
        target_id: share?.shared_with_id,
      });

      toast({
        title: 'Acesso removido',
        description: 'O usuário não tem mais acesso à sua conta',
      });

      await fetchMyShares();
      return true;
    } catch (error: any) {
      toast({
        title: 'Erro ao remover acesso',
        description: error.message,
        variant: 'destructive',
      });
      return false;
    }
  };

  /**
   * Usuário compartilhado sai do compartilhamento
   */
  const leaveShare = async (shareId: string): Promise<boolean> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      // Busca o share para notificação
      const share = sharedWithMe.find(s => s.id === shareId);

      const { error } = await supabase
        .from('account_shares')
        .delete()
        .eq('id', shareId)
        .eq('shared_with_id', user.id);

      if (error) throw error;

      // Trigger notification hook
      await triggerNotification({
        type: 'share_revoked',
        actor_id: user.id,
        share_id: shareId,
        target_id: share?.owner_id,
      });

      toast({
        title: 'Você saiu do compartilhamento',
      });

      await fetchSharedWithMe();
      return true;
    } catch (error: any) {
      toast({
        title: 'Erro ao sair do compartilhamento',
        description: error.message,
        variant: 'destructive',
      });
      return false;
    }
  };

  /**
   * Atualiza o nível de permissão de um compartilhamento
   */
  const updatePermission = async (shareId: string, permission_level: PermissionLevel): Promise<boolean> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      // Busca o share para notificação
      const share = myShares.find(s => s.id === shareId);

      const { error } = await supabase
        .from('account_shares')
        .update({ permission_level })
        .eq('id', shareId)
        .eq('owner_id', user.id);

      if (error) throw error;

      // Trigger notification hook
      await triggerNotification({
        type: 'permission_changed',
        actor_id: user.id,
        share_id: shareId,
        target_id: share?.shared_with_id,
        permission_level,
      });

      toast({
        title: 'Permissão atualizada',
        description: permission_level === 'full' 
          ? 'O usuário agora tem acesso total'
          : 'O usuário agora pode apenas visualizar',
      });

      await fetchMyShares();
      return true;
    } catch (error: any) {
      toast({
        title: 'Erro ao atualizar permissão',
        description: error.message,
        variant: 'destructive',
      });
      return false;
    }
  };

  /**
   * Cancela um convite pendente
   */
  const cancelInvitation = async (invitationId: string): Promise<boolean> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const { error } = await supabase
        .from('share_invitations')
        .delete()
        .eq('id', invitationId)
        .eq('inviter_id', user.id);

      if (error) throw error;

      toast({
        title: 'Convite cancelado',
      });

      await fetchMyInvitations();
      return true;
    } catch (error: any) {
      toast({
        title: 'Erro ao cancelar convite',
        description: error.message,
        variant: 'destructive',
      });
      return false;
    }
  };

  /**
   * Cria um compartilhamento direto com um usuário existente
   */
  const createDirectShare = async (sharedWithId: string, permission_level: PermissionLevel): Promise<AccountShare | null> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      if (user.id === sharedWithId) {
        throw new Error('Você não pode compartilhar sua conta com você mesmo');
      }

      const { data, error } = await supabase
        .from('account_shares')
        .insert({
          owner_id: user.id,
          shared_with_id: sharedWithId,
          permission_level,
          status: 'pending',
        })
        .select(`
          *,
          shared_with:profiles!account_shares_shared_with_id_fkey(id, full_name, avatar_url)
        `)
        .single();

      if (error) {
        if (error.code === '23505') {
          throw new Error('Já existe um compartilhamento com este usuário');
        }
        throw error;
      }

      // Trigger notification hook
      await triggerNotification({
        type: 'share_invitation_received',
        actor_id: user.id,
        target_id: sharedWithId,
        share_id: data.id,
        permission_level,
      });

      toast({
        title: 'Convite de compartilhamento enviado',
        description: 'Aguardando aceite do usuário',
      });

      await fetchMyShares();
      return data as AccountShare;
    } catch (error: any) {
      toast({
        title: 'Erro ao criar compartilhamento',
        description: error.message,
        variant: 'destructive',
      });
      return null;
    }
  };

  // ============================================
  // COMPUTED VALUES
  // ============================================

  // Compartilhamentos ativos onde sou owner
  const activeShares = myShares.filter(s => s.status === 'accepted');
  
  // Compartilhamentos pendentes onde sou owner (aguardando aceite)
  const pendingShares = myShares.filter(s => s.status === 'pending');
  
  // Compartilhamentos ativos onde tenho acesso
  const activeAccess = sharedWithMe.filter(s => s.status === 'accepted');
  
  // Compartilhamentos pendentes onde preciso aceitar/rejeitar
  const pendingAccess = sharedWithMe.filter(s => s.status === 'pending');

  // Verifica se o usuário tem acesso a dados de outro usuário
  const hasAccessTo = (userId: string): boolean => {
    if (userId === currentUserId) return true;
    return activeAccess.some(s => s.owner_id === userId);
  };

  // Verifica se o usuário pode escrever em dados de outro usuário
  const canWriteTo = (userId: string): boolean => {
    if (userId === currentUserId) return true;
    return activeAccess.some(s => s.owner_id === userId && s.permission_level === 'full');
  };

  // ============================================
  // RETURN
  // ============================================

  return {
    // State
    myShares,
    sharedWithMe,
    pendingInvitations,
    myInvitations,
    loading,
    currentUserId,

    // Computed
    activeShares,
    pendingShares,
    activeAccess,
    pendingAccess,

    // Queries
    fetchAll,
    fetchMyShares,
    fetchSharedWithMe,
    fetchPendingInvitations,
    fetchMyInvitations,

    // Mutations - Invitations
    createInvitation,
    acceptInvitation,
    cancelInvitation,

    // Mutations - Shares
    createDirectShare,
    acceptShare,
    rejectShare,
    revokeShare,
    leaveShare,
    updatePermission,

    // Helpers
    hasAccessTo,
    canWriteTo,
  };
}

// ============================================
// UTILITY HOOK: Check if viewing shared data
// ============================================

/**
 * Hook para verificar se o usuário está visualizando dados compartilhados
 * e obter informações sobre o dono dos dados
 */
export function useSharedDataContext() {
  const { activeAccess, currentUserId } = useSharing();

  /**
   * Retorna informações sobre o dono dos dados se for compartilhado,
   * ou null se for dados próprios
   */
  const getDataOwner = (userId: string): Pick<Profile, 'id' | 'full_name' | 'avatar_url'> | null => {
    if (userId === currentUserId) return null;
    const share = activeAccess.find(s => s.owner_id === userId);
    return share?.owner || null;
  };

  /**
   * Verifica se um dado específico é compartilhado (não é do usuário atual)
   */
  const isSharedData = (userId: string): boolean => {
    return userId !== currentUserId;
  };

  /**
   * Retorna o nível de permissão para dados de um usuário
   */
  const getPermissionLevel = (userId: string): PermissionLevel | 'owner' => {
    if (userId === currentUserId) return 'owner';
    const share = activeAccess.find(s => s.owner_id === userId);
    return (share?.permission_level as PermissionLevel) || 'view';
  };

  return {
    getDataOwner,
    isSharedData,
    getPermissionLevel,
    currentUserId,
  };
}
